import * as R from 'ramda';
import {SxProps, Theme, useTheme} from '@mui/material';
import React from 'react';
import styled from '@mui/system/styled';

/** SxProps with the MUI Theme */
export type MuiSxProps = SxProps<Theme>;
/** Record of MuiSxProps. Intended to be used for migrating MUI v4 makeStyles and not for ongoing component work */
export type MuiSxPropsRecord = Record<string, MuiSxProps>;

/** Like MUI's styled but accepts the SX prop as its styling parameter */
export const styledSx =
  <TProps extends object>(Component: Parameters<typeof styled>[0]) =>
  (sx: MuiSxProps): React.ComponentType<TProps> => {
    const SxAbleComponent = styled(Component)({});
    return (props: TProps) => <SxAbleComponent sx={sx} {...props} />;
  };

/** Given a MuiSxPropsRecord, derives a function that allows for fluently composing its styles.
 * This derived function is API compatible with the commonly-used 'classnames' library, except
 *   that it outputs an object instead of a string.
 * NOTE: the resulting merged object is only a single-level merge. Style props will be
 *  overwritten if they are redeclared in subsequent sx props. This is consistent with CSS class behavior.
 */
export function makeComposeSx<T extends MuiSxPropsRecord>(styles: T) {
  type Key = keyof T;
  /**
   * An argument to composeSx().
   * K is a key of the MuiSxPropsRecord that is being composed.
   */
  type ComposeSxArg = Key | Partial<Record<Key, boolean>> | ComposeSxArg[];

  return function composeSx(...args: ComposeSxArg[]): MuiSxProps {
    // We'll iterate over the args and build up an array of MuiSxProps to merge later
    const sxs: NonNullable<MuiSxProps>[] = [];
    const processArg = (arg: ComposeSxArg) => {
      // Base cases
      if (typeof arg === 'string') return sxs.push(styles[arg] ?? {});

      if (typeof arg === 'object' && !Array.isArray(arg))
        Object.keys(arg).forEach(key => {
          if (arg[key]) {
            sxs.push(styles[key] ?? {});
          }
        });

      // Recursive case: if the arg is an array, apply this function to each element
      if (Array.isArray(arg)) {
        return arg.forEach(processArg);
      }
    };

    // Process all args
    args.forEach(processArg);

    // Merge all the MuiSxProps together
    return R.mergeAll(sxs);
  };
}

/** Given a function that returns a MuiSxPropsRecord, returns a hook that returns the
 * resolved style objects (as SxProps) and the derived composeSx function.
 * This is intended to be used for migrating MUI v4 makeStyles and not for ongoing component work.
 *
 * @deprecated prefer inline sx props
 */
export function makeSxStyles<TStyles extends MuiSxPropsRecord>(
  makeStyles: (theme: Theme) => TStyles,
) {
  return () => {
    const theme = useTheme();
    const styles = makeStyles(theme);
    const composeSx = makeComposeSx(styles);

    return [styles, composeSx] as const;
  };
}

/** Adapts a MUI v4 classes declaration to use Sx styles targeting the appropriate child selectors */
export const classifySx = (
  /** Base name of the component without the leading .Mui (ex 'Avatar' translates to '.MuiAvatar' ) */
  componentName: string,
  /** Record of SxProps. Keys correspond to members of the classes API of the parent component */
  sx: MuiSxPropsRecord,
) => {
  const classifiedSx: MuiSxProps = {};
  Object.keys(sx).forEach(key => {
    /** @ts-expect-error TS doesn't like something about this, but the proper behavior is asserted by tests */
    classifiedSx[`& .Mui${componentName}-${key}`] = sx[key];
  });
  return classifiedSx;
};
